import { Injectable } from "@angular/core";
import { AppConsts } from "../../shared/AppConsts";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { DatePipe } from "@angular/common";
import { Platform } from "@ionic/angular";
import { SafariViewController } from '@ionic-native/safari-view-controller/ngx';
import { Deeplinks } from "@ionic-native/deeplinks/ngx";
import { StringUtils } from "@shared/utils/StringUtils";
import { CookieAuthService } from "./cookie-auth.service";

@Injectable()
export class SSOAuthService {
  private defaultHeaders = new HttpHeaders();
  private responseType = "json";
  private withCredentials = true;
  dateTimeForCookies: Date = new Date();
  dateTimeFormatted: string;
  tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  /** Refresh proactively once the session has not been confirmed for this long (token TTL is 15 min). */
  private static readonly REFRESH_AFTER_MS = 12 * 60 * 1000;
  /** When the SSO session was last known to be alive (authenticate or refresh). */
  private sessionAliveAt = 0;
  private refreshInFlight: Observable<boolean> | null = null;

  constructor(
    private _httpClient: HttpClient,
    private _cookieService: CookieService,
    private platform: Platform,
    private safariViewController: SafariViewController,
    private deeplinks: Deeplinks,
    private sessionService: CookieAuthService,
  ) {
    this.defaultHeaders = this.defaultHeaders.set("Accept", "application/json");
    this.dateTimeForCookies.setMinutes(this.dateTimeForCookies.getMinutes() + 15);

  }

  /**
   * Called before every API request. Wak.Token and Wak.RefreshToken are httpOnly
   * cookies this code can never read, so the SSO session is kept alive with
   * POST <sso>/oauth/refresh (cookie-only) once it has not been confirmed for
   * REFRESH_AFTER_MS, and again on a 401 "SSO session is missing or expired"
   * (see JwtInterceptor). A failed refresh means the SSO session is gone. The
   * mobile app's SSO cookies live in the system browser, not this webview, so
   * it keeps its own login flow and never refreshes here. See #2903.
   */
  ensureLoggedIn(): Observable<boolean> {
    if (StringUtils.isMobilePlatform(this.platform)) {
      return of(true);
    }
    if (Date.now() - this.sessionAliveAt < SSOAuthService.REFRESH_AFTER_MS) {
      return of(true);
    }
    return this.refreshSession().pipe(
      tap((ok) => {
        if (!ok) this.redirectToLogout();
      })
    );
  }

  /**
   * Cookie-only refresh at the SSO: rotates Wak.RefreshToken and re-issues
   * Wak.Token as cookies, nothing comes back in the body. Single-flight so a
   * burst of requests triggers one refresh.
   */
  refreshSession(): Observable<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    const done = new Subject<boolean>();
    this.refreshInFlight = done.asObservable();
    const ssoBase = (AppConsts.SSO.url || "").replace(/\/$/, "");
    this._httpClient
      .post(`${ssoBase}/oauth/refresh`, null, {
        withCredentials: this.withCredentials,
        responseType: <any>this.responseType,
        headers: this.defaultHeaders,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      )
      .subscribe((ok) => {
        if (ok) this.markSessionAlive();
        this.refreshInFlight = null;
        done.next(ok);
        done.complete();
      });
    return this.refreshInFlight;
  }

  markSessionAlive() {
    this.sessionAliveAt = Date.now();
  }

  authenticate(code: string) {
    return new Promise<boolean>(async (resolve) => {
      const token = await this.sessionService.getCookie(AppConsts.SSO.token);
      if (token) {
        this.markSessionAlive();
        resolve(true);
        return;
      }

      this.getandseturlparams();
      const basepath = this.getBasePath();
      const isMobile = StringUtils.isMobilePlatform(this.platform);
      const body = code
        ? {
            code,
            redirectUrl: isMobile
              ? AppConsts.SSO.redirect_uri_mobile
              : AppConsts.SSO.redirect_uri,
          }
        : { tenantId: null, superAdmin: false, admin: false, client: true };
      const url = code
        ? basepath + `/cams-service/token/sso`
        : basepath + `/cams-service/token/authenticate`;

      this._httpClient
        .post<any>(url, body, {
          withCredentials: this.withCredentials,
          responseType: <any>this.responseType,
          headers: this.defaultHeaders,
        })
        .subscribe({
          next: (res: any) => {
            // Web, no code: the endpoint answers 2xx even without a session, so a
            // response without a user still means "not logged in" (#2903).
            if (!isMobile && !code && !(res && res.userId > 0)) {
              // Leave the promise pending so bootstrap stops and the redirect wins.
              this.redirectToLogin();
              return;
            }
            this.markSessionAlive();
            resolve(true);
          },
          error: () => {
            if (isMobile) {
              resolve(false);
              return;
            }
            // Web: leave the promise pending so bootstrap stops and the redirect wins.
            if (code) {
              this.redirectToLogout();
            } else {
              this.redirectToLogin();
            }
          },
        });
    });
  }

  initialize() {
    return new Promise<boolean>((resolve, reject) => {
      let basepath = this.getBasePath();
      this._httpClient
        .get(basepath + "/cams-service/token/sso/configuration", {
          withCredentials: this.withCredentials,
          responseType: <any>this.responseType,
          headers: this.defaultHeaders,
        })
        .subscribe({
          next: (result) => {
            AppConsts.SSO.url = result["url"];
            AppConsts.SSO.client_id = result["client_id"];
            resolve(true);
          },
          error: (ex) => {
            reject(false);
          },
        });
    });
  }

  redirectToLogin() {
    const isMobile = StringUtils.isMobilePlatform(this.platform);
    const redirect_uri = isMobile ? AppConsts.SSO.redirect_uri_mobile : AppConsts.SSO.redirect_uri;
    const loginUrl = `${AppConsts.SSO.url}/oauth/authorize?response_type=${AppConsts.SSO.response_type}&client_id=${AppConsts.SSO.client_id}&redirect_uri=${redirect_uri}`;
    if (isMobile) {
      this.safariViewController.isAvailable().then((available) => {
        if (available) {
          this.safariViewController
            .show({
              url: loginUrl,
              hidden: false, // default false. You can use this to load cookies etc in the background (see issue #1 for details).
              animated: false, // default true, note that 'hide' will reuse this preference (the 'Done' button will always animate though)
              transition: 'curl', // (this only works in iOS 9.1/9.2 and lower) unless animated is false you can choose from: curl, flip, fade, slide (default)
              controlTintColor: '#ffffff', // on iOS 10+ you can override the default tintColor
            })
            .subscribe({
              next: (result: any) => {
                if (result.event === 'loaded') console.log('Loaded');
              },
              error: (error: any) => console.error(error),
            });
        } else {
          // potentially powered by InAppBrowser because that (currently) clobbers window.open
          window.location.href = loginUrl;
        }
      });
    } else {
      window.localStorage.removeItem('Abp.TenantId');
      window.location.href = loginUrl;
    }
  }

  redirectToLogout() {
    const isMobile = StringUtils.isMobilePlatform(this.platform);
    const redirect_uri = isMobile ? AppConsts.SSO.redirect_uri_mobile : AppConsts.SSO.logout_redirect_uri;
    const logoutUrl = `${AppConsts.SSO.url}/logout?response_type=${AppConsts.SSO.response_type}&client_id=${AppConsts.SSO.client_id}&redirect_uri=${redirect_uri}`;
    if (isMobile) {
      this.safariViewController
        .show({
          url: logoutUrl,
          hidden: false, // default false. You can use this to load cookies etc in the background (see issue #1 for details).
          animated: false, // default true, note that 'hide' will reuse this preference (the 'Done' button will always animate though)
          transition: 'curl', // (this only works in iOS 9.1/9.2 and lower) unless animated is false you can choose from: curl, flip, fade, slide (default)
          controlTintColor: '#ffffff', // on iOS 10+ you can override the default tintColor
        })
        .subscribe({
          next: async (result: any) => {
            if (result.event === 'loaded') {
              // IMPORTANT: Timeout is need to give it some time to properly clear browser session cookies,
              // as it sometimes return success response without properly loading the url for both Android & iOS
              setTimeout(() => {
                this.safariViewController.hide();
                window.location.href = './account/select-country';
              }, 1000);
            }
          },
          error: (error: any) => console.error(error),
        });
    } else {
      window.location.href = logoutUrl;
    }
  }

  redirectToResetPassword() {
    const isMobile = StringUtils.isMobilePlatform(this.platform);
    const redirect_uri = isMobile ? AppConsts.SSO.redirect_uri_mobile : AppConsts.SSO.redirect_uri;
    window.location.href = `${AppConsts.SSO.url}/reset-password?response_type=${AppConsts.SSO.response_type}&client_id=${AppConsts.SSO.client_id}&redirect_uri=${redirect_uri}`;
  }

  logout() {
    abp.ui.clearBusy();
    if (StringUtils.isMobilePlatform(this.platform)) {
      window.localStorage.removeItem('Abp.TenantId');
      window.localStorage.removeItem('companyId');
      // Mobile run on the host 'mobile.wakandi.com' so above code doesn't exactly works there, below we're directly specifying the hostname
      this._cookieService.deleteAll("/", 'wakandi.com', true, "None");
      this._cookieService.deleteAll("/", 'wakandi.com', true, "Lax");

      abp.utils.deleteCookie(AppConsts.authorization.encryptedAuthTokenName, '/');
      abp.utils.deleteCookie(AppConsts.authorization.AuthToken, '/');
      abp.utils.deleteCookie(AppConsts.SSO.token, '/');
      abp.utils.deleteCookie(AppConsts.SSO.refreshToken, '/');
    } else {
      window.localStorage.removeItem('Abp.TenantId');
      this._cookieService.deleteAll("/", window.location.host, true, "None");
      this._cookieService.deleteAll("/", window.location.host, true, "Lax");
    }
    if (window.self === window.top) {
      this.redirectToLogout();
    }
  }

  ssoListener(callback: (code?: string) => void) {
    return this.deeplinks.route({ "/sso-mobile": "login" }).subscribe({
      next: (match) => {
        this.safariViewController.hide();
        callback(match.$args.code);
      },
      error: (nomatch) => {
        // nomatch.$link - the full link data
        console.error("Got a deeplink that didn't match", nomatch);
        callback(null);
      },
    });
  }

  async canActivate(): Promise<boolean> {
    var ssoTOken = await this.sessionService.getCookie(AppConsts.SSO.token);
    if (ssoTOken) {
      return true;
    }
    return false;
  }

  canMatch(): boolean {
    return true;
  }

  getandseturlparams() {
    var url = new URL(window.location.href);
    var token = url.searchParams.get("token");
    var _t = url.searchParams.get("_t");
    var _u = url.searchParams.get("_u");
    var _code = url.searchParams.get("_code");

    if (token != "" && token != null)
      this._cookieService.set("token", token, this.dateTimeForCookies);
    else
      this._cookieService.delete("token");

    if (_t != "" && _t != null)
      this._cookieService.set("_t", _t, this.dateTimeForCookies);
    else
      this._cookieService.delete("_t");

    if (_u != "" && _u != null)
      this._cookieService.set("_u", _u, this.dateTimeForCookies);
    else
      this._cookieService.delete("_u");

    if (_code != "" && _code != null)
      this._cookieService.set("_code", _code, this.dateTimeForCookies);
    else
      this._cookieService.delete("_code");
  }
  /**
   * Returns base path to use for mobile, as it depends on the environment selected
   * it'll be empty for Web, as it takes the base path from proxy.conf.ts
   * @returns 
   */
  getBasePath(): string {
    let basepath = "";
    if (StringUtils.isMobilePlatform(this.platform))
      basepath = AppConsts.remoteServiceBaseUrl.replace('/api', '');
    return basepath;
  }
}
